import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./Work.module.css";
import {
  User,
  Image,
  PenTool,
  Laptop,
  Presentation,
  LaptopMinimalCheck,
  ArrowUpFromLine,
  ListStart,
  ShieldCheck,
  MessageCircleMore
} from "lucide-react";

const StepsOfProcess = () => {
  const stepsData = [
    {
      number: 1,
      titleOfStep: "Konsultacja",
      icon: <User size={60} />,
      description:
        "Poznajemy Twoje potrzeby, cele oraz oczekiwania dotyczące projektu. Rozmawiamy o funkcjach, wyglądzie i rozwiązaniach, które najlepiej sprawdzą się w Twoim przypadku."
    },
    {
      number: 2,
      titleOfStep: "Oferta i wycena",
      icon: <Image size={60} />,
      description:
        "Na podstawie zebranych informacji przygotowujemy propozycję rozwiązania wraz z zakresem prac i indywidualną wyceną."
    },
    {
      number: 3,
      titleOfStep: "Rozpoczęcie projektu",
      icon: <PenTool size={60} />,
      description:
        "Po akceptacji oferty podpisujemy umowę i zbieramy niezbędne informacje oraz materiały potrzebne do realizacji projektu."
    },
    {
      number: 4,
      titleOfStep: "Projektowanie i kodowanie",
      icon: <Laptop size={80} />,
      description:
        "Projektujemy wygląd strony, a następnie kodujemy ją od podstaw, dbając o funkcjonalność, responsywność i dopasowanie do Twoich potrzeb."
    },
    {
      number: 5,
      titleOfStep: "Prezentacja projektu",
      icon: <Presentation size={60} />,
      description:
        "Przedstawiamy gotową wersję strony i omawiamy zastosowane rozwiązania."
    },
    {
      number: 6,
      titleOfStep: "Testy i uwagi",
      icon: <MessageCircleMore size={60} />,
      description:
        "Otrzymujesz możliwość przetestowania strony oraz zgłoszenia ewentualnych uwag i propozycji zmian."
    },
    {
      number: 7,
      titleOfStep: "Wprowadzanie poprawek",
      icon: <LaptopMinimalCheck size={60} />,
      description:
        "Analizujemy zgłoszone uwagi i wprowadzamy ustalone poprawki, aby finalna wersja spełniała Twoje oczekiwania."
    },
    {
      number: 8,
      titleOfStep: "Publikacja strony",
      icon: <ArrowUpFromLine size={60} />,
      description:
        "Po akceptacji projektu przygotowujemy stronę do publikacji i wdrażamy ją pod wskazaną domeną."
    },
    {
      number: 9,
      titleOfStep: "Optymalizacja SEO",
      icon: <ListStart size={60} />,
      description:
        "Dbamy o podstawowe przygotowanie strony pod wyszukiwarki – wykonujemy optymalizację techniczną oraz pomagamy w przygotowaniu treści przyjaznych SEO."
    },
    {
      number: 10,
      titleOfStep: "Wsparcie i rozwój",
      icon: <ShieldCheck size={60} />,
      description:
        "Po zakończeniu projektu możesz liczyć na nasze wsparcie techniczne, aktualizacje oraz pomoc przy dalszym rozwoju strony."
    }
  ];

  const totalSteps = stepsData.length;
  const [currentStep, setCurrentStep] = useState(0);
  const autoSlideRef = useRef();
  const hoverTimeoutRef = useRef();
  const stepRef = useRef(null);
  const [stepHeight, setStepHeight] = useState(0);

  // Automatyczne przejście
  const nextStep = useCallback(() => {
    if (!totalSteps) return;
    setCurrentStep((prev) => (prev + 1) % totalSteps);
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => (prev - 1 + totalSteps) % totalSteps);
  }, [totalSteps]);

  const goToStep = useCallback((index) => {
    setCurrentStep(index);
  }, []);

  const stopAutoSlide = useCallback(() => {
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = null;
    }
  }, []);

  const startAutoSlide = useCallback(() => {
    stopAutoSlide();
    autoSlideRef.current = setInterval(nextStep, 5000);
  }, [stopAutoSlide, nextStep]);

  useEffect(() => {
    startAutoSlide();

    return stopAutoSlide;
  }, [startAutoSlide, stopAutoSlide]);

  // Hover
  const handleMouseEnter = () => {
    stopAutoSlide();
    hoverTimeoutRef.current = setTimeout(startAutoSlide, 10000);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    startAutoSlide();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (stepRef.current) {
        setStepHeight(stepRef.current.offsetHeight);
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [currentStep]);

  return (
    <section id="work" className="sectionContainer light">
      <div className="sectionTitle">
        <h2 className={styles.titleOfSection}>
          Jak wygląda proces tworzenia strony internetowej?
        </h2>
      </div>

      <div
        className={styles.workContent}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.numberOfSteps} style={{ height: stepHeight }}>
          {stepsData.map((step, index) => (
            <div
              key={index}
              ref={index === currentStep ? stepRef : null}
              className={`${styles.step} ${
                index === currentStep ? styles.stepActive : ""
              }`}
            >
              <div className={styles.stepNumber}>
                {step.number}
                {". "}
                {step.titleOfStep}
              </div>
              <div className={styles.stepItem}>
                <div className={styles.stepIcon}>{step.icon}</div>
                <div className={styles.stepDescription}>
                  <p>{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.arrow}>
          <i className="fa fa-arrow-left" onClick={prevStep} />
          <i className="fa fa-arrow-right" onClick={nextStep} />
        </div>

        <div className={styles.dotsContainer}>
          {stepsData.map((_, index) => (
            <span
              key={index}
              className={`${styles.dot} ${
                index === currentStep ? styles.dotActive : ""
              }`}
              onClick={() => goToStep(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StepsOfProcess;
